import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import { Settings, Save, Upload, Palette, CreditCard, Check, X, Image, Loader2, Trash2, Zap, ArrowRight, Shield, Crown, User, Mail, Camera, FileCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CoachSettings = () => {
  const { user, getAuthHeaders, updateUser } = useAuth();
  const { theme, updateTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploadingVerification, setUploadingVerification] = useState(false);
  const logoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const idDocInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const [idDocUrl, setIdDocUrl] = useState('');
  const [selfieUrl, setSelfieUrl] = useState('');
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    business_name: user?.business_name || '',
    phone: user?.phone || '',
    bio: user?.bio || ''
  });
  const [coachPhoto, setCoachPhoto] = useState(user?.photo_url || '');
  const [brandingData, setBrandingData] = useState({
    logo_url: user?.branding?.logo_url || '',
    primary_color: user?.branding?.primary_color || '#D4AF37',
    secondary_color: user?.branding?.secondary_color || '#0F5E3A',
    accent_color: user?.branding?.accent_color || '#FFFFFF'
  });

  const canWhiteLabel = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'unlimited';

  // Fetch verification status
  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const res = await axios.get(`${API}/coach/verification-status`, {
          headers: getAuthHeaders()
        });
        setVerificationStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch verification status:', err);
      }
    };
    fetchVerificationStatus();
  }, [getAuthHeaders]);

  // Handle Stripe payment return - watch for URL changes
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const sessionId = searchParams.get('session_id');
    
    console.log('[CoachSettings] Payment return check:', { success, cancelled, sessionId });
    
    if (success === 'true' && sessionId) {
      console.log('[CoachSettings] Processing payment success for session:', sessionId);
      // Payment successful - process and refresh
      processPaymentSuccess(sessionId);
      // Clean URL after processing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('success');
      newParams.delete('session_id');
      setSearchParams(newParams, { replace: true });
    } else if (cancelled === 'true') {
      toast.info('Payment cancelled');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('cancelled');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams]);

  const processPaymentSuccess = async (sessionId) => {
    console.log('[CoachSettings] processPaymentSuccess called with sessionId:', sessionId);
    
    try {
      // First, simulate/confirm the payment in beta mode
      if (sessionId) {
        console.log('[CoachSettings] Calling simulate-success endpoint...');
        const simRes = await axios.post(`${API}/checkout/simulate-success/${sessionId}`, {}, { headers: getAuthHeaders() });
        console.log('[CoachSettings] simulate-success response:', simRes.data);
      }
      
      // Then refresh user data from backend
      console.log('[CoachSettings] Fetching updated coach profile...');
      const response = await axios.get(`${API}/coach/me`, { headers: getAuthHeaders() });
      console.log('[CoachSettings] coach/me response:', response.data);
      
      if (response.data) {
        // Force full user state replacement with new data
        const newUserData = { ...response.data, role: 'coach' };
        console.log('[CoachSettings] Updating user state with:', newUserData);
        updateUser(newUserData);
        
        if (response.data.subscription?.plan === 'pro') {
          toast.success('🎉 Plan upgraded to Pro! Welcome to the Pro features.');
        } else {
          toast.success('Payment processed successfully!');
        }
      }
    } catch (error) {
      console.error('[CoachSettings] Payment processing error:', error);
      // Try just refreshing user data as fallback
      try {
        console.log('[CoachSettings] Fallback: fetching coach profile...');
        const response = await axios.get(`${API}/coach/me`, { headers: getAuthHeaders() });
        console.log('[CoachSettings] Fallback response:', response.data);
        
        if (response.data) {
          const newUserData = { ...response.data, role: 'coach' };
          updateUser(newUserData);
          if (response.data.subscription?.plan === 'pro') {
            toast.success('🎉 Plan upgraded to Pro!');
          }
        }
      } catch (e) {
        console.error('[CoachSettings] Fallback also failed, reloading page');
        toast.error('Processing payment... Please wait.');
        setTimeout(() => window.location.reload(), 1500);
      }
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/coach/profile`, profileData, { headers: getAuthHeaders() });
      updateUser(response.data);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  const saveBranding = async () => {
    if (!canWhiteLabel) {
      toast.error('White-label is only available on Pro and Unlimited plans');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(`${API}/coach/branding`, brandingData, { headers: getAuthHeaders() });
      updateTheme({
        logoUrl: response.data.logo_url,
        primaryColor: response.data.primary_color,
        secondaryColor: response.data.secondary_color,
        accentColor: response.data.accent_color
      });
      toast.success('Branding updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo file too large. Maximum size is 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, WebP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingLogo(true);
    try {
      const response = await axios.post(`${API}/coach/upload-logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setBrandingData({...brandingData, logo_url: response.data.logo_url});
      updateUser({ branding: { ...user?.branding, logo_url: response.data.logo_url } });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
    setUploadingLogo(false);
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Delete your logo?')) return;
    
    try {
      await axios.delete(`${API}/coach/logo`, { headers: getAuthHeaders() });
      setBrandingData({...brandingData, logo_url: ''});
      updateUser({ branding: { ...user?.branding, logo_url: '' } });
      toast.success('Logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  // Helper to get full logo URL
  const getLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    // Legacy support for old URLs without /api prefix
    if (url.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${url}`;
    }
    return url;
  };

  // Helper to get full photo URL
  const getPhotoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    if (url.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${url}`;
    }
    return url;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo file too large. Maximum size is 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, WebP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingPhoto(true);
    try {
      const response = await axios.post(`${API}/coach/upload-photo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setCoachPhoto(response.data.photo_url);
      updateUser({ photo_url: response.data.photo_url });
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
    setUploadingPhoto(false);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Delete your profile photo?')) return;
    
    try {
      await axios.delete(`${API}/coach/photo`, { headers: getAuthHeaders() });
      setCoachPhoto('');
      updateUser({ photo_url: '' });
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Verification functions
  const handleRequestEmailVerification = async () => {
    try {
      await axios.post(`${API}/coach/request-email-verification`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    }
  };

  const handleVerificationDocUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, WebP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);

    setUploadingVerification(true);
    try {
      const response = await axios.post(`${API}/coach/upload-verification-document?doc_type=${docType}`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (docType === 'id') {
        setIdDocUrl(response.data.url);
      } else {
        setSelfieUrl(response.data.url);
      }
      toast.success(`${docType === 'id' ? 'ID document' : 'Selfie'} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
    setUploadingVerification(false);
  };

  const handleSubmitIdentityVerification = async () => {
    if (!idDocUrl || !selfieUrl) {
      toast.error('Please upload both ID document and selfie');
      return;
    }

    try {
      await axios.post(`${API}/coach/submit-identity-verification`, {
        id_document_url: idDocUrl,
        selfie_url: selfieUrl
      }, {
        headers: getAuthHeaders()
      });
      
      setVerificationStatus(prev => ({
        ...prev,
        identity_verification_status: 'pending'
      }));
      toast.success('Identity verification submitted for review!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    }
  };

  const PLANS = {
    standard: { 
      name: 'Standard', 
      price: '$9.99/mo', 
      priceValue: 9.99,
      athletes: 'Unlimited', 
      features: ['Unlimited athletes', 'Training plans', 'Drill library', 'Video links', 'Session scheduling'] 
    },
    pro: { 
      name: 'Pro', 
      price: '$14.99/mo', 
      priceValue: 14.99,
      athletes: 'Unlimited', 
      features: ['Everything in Standard', 'White-label branding', 'Custom logo & colors', 'Financial dashboard', 'Priority support'] 
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: Settings },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
  ];

  return (
    <div className="space-y-6" data-testid="coach-settings">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account and branding</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-primary text-black' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border/40 rounded-sm p-6">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Profile Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Full Name</label>
                <Input
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Business Name</label>
                <Input
                  value={profileData.business_name}
                  onChange={(e) => setProfileData({...profileData, business_name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Phone</label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Email</label>
                <Input value={user?.email || ''} disabled className="opacity-50" />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Bio</label>
              <Textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                placeholder="Tell your clients about yourself..."
              />
            </div>
            
            {/* Profile Photo Section */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Profile Photo</label>
              <p className="text-xs text-muted-foreground mb-3">This photo will be displayed to parents in their dashboard</p>
              <div className="flex items-center gap-4">
                {/* Photo Preview */}
                <div className="w-24 h-24 border border-border rounded-full flex items-center justify-center bg-muted/30 overflow-hidden">
                  {coachPhoto ? (
                    <img 
                      src={getPhotoUrl(coachPhoto)} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="text-muted-foreground" size={32} />
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={photoInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Photo
                      </>
                    )}
                  </Button>
                  {coachPhoto && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleDeletePhoto}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <Button onClick={saveProfile} disabled={saving} className="bg-primary text-black">
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        )}

        {activeTab === 'verification' && (
          <div className="space-y-6">
            <div>
              <h2 className="font-heading text-xl uppercase mb-2">Coach Verification</h2>
              <p className="text-muted-foreground text-sm">
                Verify your identity to increase the reliability of your evaluations. 
                Athletes and parents will see your verification level.
              </p>
            </div>

            {/* Current Status */}
            <div className="bg-muted/30 p-4 rounded-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">Verification Level</span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${
                  verificationStatus?.verification_level === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                  verificationStatus?.verification_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {verificationStatus?.verification_level?.toUpperCase() || 'LOW'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {verificationStatus?.verification_level === 'high' 
                  ? 'Your evaluations have the highest reliability.' 
                  : verificationStatus?.verification_level === 'medium'
                    ? 'Complete identity verification to reach HIGH reliability.'
                    : 'Verify your email and identity to increase reliability.'}
              </p>
            </div>

            {/* Email Verification */}
            <div className="border border-border/40 rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">Email Verification</h3>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                {verificationStatus?.email_verified ? (
                  <span className="flex items-center gap-1 text-emerald-400 text-sm">
                    <Check size={16} /> Verified
                  </span>
                ) : (
                  <Button 
                    size="sm" 
                    onClick={handleRequestEmailVerification}
                    className="bg-primary text-black"
                  >
                    Verify Email
                  </Button>
                )}
              </div>
            </div>

            {/* Identity Verification */}
            <div className="border border-border/40 rounded-sm p-4">
              <div className="flex items-center gap-3 mb-4">
                <FileCheck size={20} className="text-muted-foreground" />
                <div>
                  <h3 className="font-medium">Identity Verification</h3>
                  <p className="text-xs text-muted-foreground">
                    Upload a government-issued ID and a selfie for verification
                  </p>
                </div>
              </div>

              {verificationStatus?.identity_verified ? (
                <div className="flex items-center gap-2 text-emerald-400">
                  <Check size={16} />
                  <span>Identity Verified</span>
                </div>
              ) : verificationStatus?.identity_verification_status === 'pending' ? (
                <div className="flex items-center gap-2 text-yellow-400">
                  <AlertCircle size={16} />
                  <span>Verification pending review</span>
                </div>
              ) : verificationStatus?.identity_verification_status === 'rejected' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-red-400">
                    <X size={16} />
                    <span>Verification rejected - please resubmit</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* ID Document Upload */}
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">
                      ID Document (Passport, Driver's License, or National ID)
                    </label>
                    <div className="flex items-center gap-3">
                      {idDocUrl ? (
                        <div className="flex items-center gap-2 text-emerald-400">
                          <Check size={16} />
                          <span className="text-sm">Document uploaded</span>
                        </div>
                      ) : (
                        <>
                          <input
                            ref={idDocInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleVerificationDocUpload(e, 'id')}
                            className="hidden"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => idDocInputRef.current?.click()}
                            disabled={uploadingVerification}
                          >
                            {uploadingVerification ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="mr-2 h-4 w-4" />
                            )}
                            Upload ID
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Selfie Upload */}
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">
                      Selfie (holding your ID document)
                    </label>
                    <div className="flex items-center gap-3">
                      {selfieUrl ? (
                        <div className="flex items-center gap-2 text-emerald-400">
                          <Check size={16} />
                          <span className="text-sm">Selfie uploaded</span>
                        </div>
                      ) : (
                        <>
                          <input
                            ref={selfieInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleVerificationDocUpload(e, 'selfie')}
                            className="hidden"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => selfieInputRef.current?.click()}
                            disabled={uploadingVerification}
                          >
                            {uploadingVerification ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Camera className="mr-2 h-4 w-4" />
                            )}
                            Upload Selfie
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  {idDocUrl && selfieUrl && (
                    <Button 
                      onClick={handleSubmitIdentityVerification}
                      className="bg-primary text-black"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      Submit for Verification
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
              <h4 className="text-blue-400 font-medium mb-2">Why verify?</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Higher evaluation reliability displayed to parents</li>
                <li>• Increased trust and credibility</li>
                <li>• Your documents are securely stored and only used for verification</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">White-Label Branding</h2>
              {!canWhiteLabel && (
                <span className="text-xs bg-yellow-500/10 text-yellow-400 px-3 py-1 rounded">
                  Upgrade to Pro to unlock
                </span>
              )}
            </div>
            
            <div className={!canWhiteLabel ? 'opacity-50 pointer-events-none' : ''}>
              <div className="space-y-4">
                {/* Logo Upload Section */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Logo</label>
                  <div className="flex items-center gap-4">
                    {/* Logo Preview */}
                    <div className="w-20 h-20 border border-border rounded-sm flex items-center justify-center bg-muted/30 overflow-hidden">
                      {brandingData.logo_url ? (
                        <img 
                          src={getLogoUrl(brandingData.logo_url)} 
                          alt="Logo" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Image className="text-muted-foreground" size={24} />
                      )}
                    </div>
                    
                    {/* Upload Controls */}
                    <div className="flex-1 space-y-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                        >
                          {uploadingLogo ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="mr-2 h-4 w-4" /> Upload Logo</>
                          )}
                        </Button>
                        {brandingData.logo_url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleDeleteLogo}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">PNG, JPG, WebP up to 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Or use URL */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Or use Logo URL</label>
                  <Input
                    value={brandingData.logo_url?.includes('/uploads/') ? '' : brandingData.logo_url}
                    onChange={(e) => setBrandingData({...brandingData, logo_url: e.target.value})}
                    placeholder="https://your-logo-url.com/logo.png"
                    disabled={brandingData.logo_url?.includes('/uploads/')}
                  />
                  {brandingData.logo_url?.includes('/uploads/') && (
                    <p className="text-xs text-muted-foreground mt-1">Remove uploaded logo to use URL instead</p>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={brandingData.primary_color}
                        onChange={(e) => setBrandingData({...brandingData, primary_color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={brandingData.primary_color}
                        onChange={(e) => setBrandingData({...brandingData, primary_color: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Secondary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={brandingData.secondary_color}
                        onChange={(e) => setBrandingData({...brandingData, secondary_color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={brandingData.secondary_color}
                        onChange={(e) => setBrandingData({...brandingData, secondary_color: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Accent Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={brandingData.accent_color}
                        onChange={(e) => setBrandingData({...brandingData, accent_color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={brandingData.accent_color}
                        onChange={(e) => setBrandingData({...brandingData, accent_color: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-sm">
                  <p className="text-sm text-muted-foreground mb-2">Preview</p>
                  <div className="flex items-center gap-3">
                    {brandingData.logo_url ? (
                      <img src={getLogoUrl(brandingData.logo_url)} alt="Logo" className="h-8 w-auto" />
                    ) : (
                      <div className="w-8 h-8 rounded" style={{ backgroundColor: brandingData.primary_color }}></div>
                    )}
                    <span className="font-semibold">{profileData.business_name || 'Your Business'}</span>
                  </div>
                </div>

                <Button onClick={saveBranding} disabled={saving || !canWhiteLabel} className="bg-primary text-black">
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Branding'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Subscription</h2>
            
            {/* Current Plan Banner */}
            <div className={`border rounded-sm p-5 ${
              (user?.subscription?.plan || 'standard').toLowerCase() === 'pro' 
                ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30' 
                : 'bg-primary/10 border-primary/30'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {(user?.subscription?.plan || 'standard').toLowerCase() === 'pro' ? (
                    <Crown className="text-amber-400" size={24} />
                  ) : (
                    <Shield className="text-primary" size={24} />
                  )}
                  <div>
                    <span className="text-lg font-semibold capitalize">{user?.subscription?.plan || 'Standard'} Plan</span>
                    <p className="text-sm text-muted-foreground">
                      {user?.subscription?.status === 'active' ? 'Active subscription' : 'Subscription status: ' + (user?.subscription?.status || 'N/A')}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded text-sm uppercase font-semibold ${
                  user?.subscription?.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {user?.subscription?.status || 'N/A'}
                </span>
              </div>
              {user?.subscription?.end_date && (
                <p className="text-sm text-muted-foreground mt-2">
                  Valid until: {new Date(user.subscription.end_date).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Plans Comparison */}
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(PLANS).map(([key, plan]) => {
                const isCurrentPlan = (user?.subscription?.plan || 'standard').toLowerCase() === key;
                const isPro = key === 'pro';
                
                return (
                  <div
                    key={key}
                    className={`border rounded-sm p-5 relative ${
                      isCurrentPlan 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/30' 
                        : isPro 
                          ? 'border-amber-500/50 bg-gradient-to-b from-amber-500/5 to-transparent hover:border-amber-500'
                          : 'border-border hover:border-primary/50'
                    } transition-all`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-4">
                        <span className="bg-primary text-black text-xs px-2 py-1 rounded uppercase font-semibold">
                          Current Plan
                        </span>
                      </div>
                    )}
                    {isPro && !isCurrentPlan && (
                      <div className="absolute -top-3 right-4">
                        <span className="bg-amber-500 text-black text-xs px-2 py-1 rounded uppercase font-semibold">
                          Recommended
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-3 mt-2">
                      {isPro ? (
                        <Crown className="text-amber-400" size={20} />
                      ) : (
                        <Shield className="text-primary" size={20} />
                      )}
                      <h3 className="font-heading text-xl uppercase">{plan.name}</h3>
                    </div>
                    
                    <p className={`text-3xl font-bold mb-1 ${isPro ? 'text-amber-400' : 'text-primary'}`}>
                      {plan.price}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {plan.athletes} athletes
                    </p>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="text-sm flex items-center gap-2">
                          <Check size={14} className={isPro ? 'text-amber-400' : 'text-primary'} />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {!isCurrentPlan && (
                      <Button 
                        onClick={async () => {
                          if (isPro) {
                            // Upgrade to Pro
                            setUpgrading(true);
                            try {
                              const res = await axios.post(`${API}/coach/upgrade`, {
                                plan: 'pro',
                                origin_url: window.location.origin
                              }, { headers: getAuthHeaders() });
                              
                              if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              } else {
                                toast.success('Plan upgraded successfully!');
                                window.location.reload();
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to upgrade plan');
                            }
                            setUpgrading(false);
                          } else {
                            // Downgrade to Standard
                            toast.info('To downgrade, please contact support@meybra.com');
                          }
                        }}
                        disabled={upgrading}
                        className={`w-full ${
                          isPro 
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold' 
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        data-testid={`${key}-plan-btn`}
                      >
                        {upgrading ? (
                          <Loader2 className="animate-spin mr-2" size={16} />
                        ) : isPro ? (
                          <Zap size={16} className="mr-2" />
                        ) : null}
                        {isPro ? 'Upgrade to Pro' : 'Downgrade'}
                      </Button>
                    )}
                    
                    {isCurrentPlan && (
                      <div className="w-full py-2 text-center text-sm text-muted-foreground border border-border rounded-sm">
                        Your current plan
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="bg-muted/30 rounded-sm p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Need help with your subscription? Contact us at{' '}
                <a href="mailto:support@meybra.com" className="text-primary hover:underline">
                  support@meybra.com
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachSettings;
